// useCrestUpload — wire a chosen File through validation, compression,
// and a Supabase Storage upload to the public `family-crests` bucket,
// then write the resulting public URL to family_crest.image_url.
//
// Mirrors `useAvatarUpload` 1:1 with three knobs turned:
//
//   * bucket:          "family-crests" (public, 5 MiB, png/jpeg/webp)
//                      — new bucket added in migration 04980, RLS lets
//                        any family member of the path's leading
//                        segment insert.
//   * object path:     `<family_id>/crest-<timestamp>-<rand>.webp`
//                      — same trailing-suffix pattern as avatars so
//                        there is no UPDATE policy dependency. Each
//                        upload lands at a fresh path; the row's
//                        image_url simply points at the latest one.
//   * DB column:       family_crest.image_url (and the row is keyed
//                      by family_id, so we UPSERT with that PK).
//
// Why a public bucket instead of the private `family-photos` bucket
// that already exists: crests show up on every chat-thread header
// and on multiple pages. Minting a signed URL on every render would
// double the network roundtrips and require us to handle 60-second
// expiry edge cases. Public reads are correct here (the crest is
// not PII — it's the family's signature, the way an org logo is).

import { useCallback, useState } from "react";
import { supabase, isAuthConfigured } from "./supabase";
import {
  MAX_IMAGE_BYTES,
  prepareImage,
  randSuffix,
  friendlyUploadErr,
} from "./imageUpload";

const BUCKET = "family-crests";

export type CrestUploadPhase =
  | "idle"
  | "compressing"
  | "uploading"
  | "saving"
  | "done"
  | "error";

export type CrestUploadState =
  | { phase: "idle" }
  | { phase: "compressing" }
  | { phase: "uploading" }
  | { phase: "saving" }
  | { phase: "done"; url: string }
  | { phase: "error"; message: string };

export type CrestUploadApi = {
  state: CrestUploadState;
  /** Replace the family's crest. On success the new public URL is
   *  written to family_crest.image_url in the same flow; the caller
   *  then calls cf.refresh() (or relies on the realtime hook) so the
   *  change is visible. */
  upload: (
    familyId: string,
    file: File,
  ) => Promise<{ url: string } | null>;
  /** Clear the family's crest. Returns true when family_crest.image_url
   *  has been set back to null; the caller then refreshes so the
   *  fallback renders. */
  remove: (familyId: string) => Promise<boolean>;
  reset: () => void;
};

export function useCrestUpload(): CrestUploadApi {
  const [state, setState] = useState<CrestUploadState>({ phase: "idle" });

  const reset = useCallback(() => {
    setState({ phase: "idle" });
  }, []);

  const upload = useCallback(
    async (familyId: string, file: File): Promise<{ url: string } | null> => {
      if (!isAuthConfigured()) {
        setState({ phase: "error", message: "尚未設定 Supabase 環境變數。" });
        return null;
      }
      if (!familyId) {
        setState({ phase: "error", message: "未選擇家庭，無法上傳家徽。" });
        return null;
      }
      if (!file.type.startsWith("image/")) {
        setState({ phase: "error", message: "請選擇圖片檔案（PNG / JPEG / WebP）。" });
        return null;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setState({
          phase: "error",
          message: `圖片太大（${(file.size / 1024 / 1024).toFixed(1)} MiB），上限為 5 MiB。`,
        });
        return null;
      }

      // 1. Compress / re-encode to webp in the browser. The same
      //    helper the avatar uploader uses, so both pipelines
      //    behave identically on edge cases (HEIC, very small
      //    PNGs, etc).
      setState({ phase: "compressing" });
      const prepared = await prepareImage(file);

      // 2. Upload to a per-family folder. The RLS policy
      //    `family crests write` (migration 04980) checks that
      //    split_part(name, '/', 1)::uuid is a family the caller
      //    belongs to, so the folder name acts as the family
      //    scope token.
      setState({ phase: "uploading" });
      const objectPath = `${familyId}/crest-${Date.now()}-${randSuffix()}.webp`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(objectPath, prepared, {
          contentType: "image/webp",
          upsert: false,
          cacheControl: "3600",
        });

      if (uploadErr) {
        setState({ phase: "error", message: friendlyUploadErr(uploadErr.message, { asset: "家徽" }) });
        return null;
      }

      // 3. Resolve the public URL (bucket is public per migration 04980).
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(objectPath);
      const url = pub.publicUrl;
      if (!url) {
        setState({ phase: "error", message: "上傳成功但無法取得公開連結。" });
        return null;
      }

      // 4. Persist the URL on family_crest. UPSERT lets this work
      //    whether or not a crest row exists yet for the family
      //    (the bootstrap trigger creates one on family creation,
      //    but defending against the uncreated case avoids a
      //    silent null-pointer on a manual family insert). RLS
      //    family_crest_family_rw (migration 014) gates the
      //    write to family members.
      //
      //    family_crest only carries the image now (migration 05000
      //    dropped the redundant `name` column — families.name is
      //    the single source of truth), so the upsert is just the
      //    URL keyed by family_id.
      setState({ phase: "saving" });
      const { error: patchErr } = await supabase
        .from("family_crest")
        .upsert(
          { family_id: familyId, image_url: url },
          { onConflict: "family_id" },
        );
      if (patchErr) {
        setState({ phase: "error", message: "已上傳但寫入家徽失敗：" + patchErr.message });
        return null;
      }

      setState({ phase: "done", url });
      return { url };
    },
    [],
  );

  const remove = useCallback(
    async (familyId: string): Promise<boolean> => {
      if (!isAuthConfigured()) {
        setState({ phase: "error", message: "尚未設定 Supabase 環境變數。" });
        return false;
      }
      if (!familyId) return false;
      setState({ phase: "saving" });
      const { error } = await supabase
        .from("family_crest")
        .update({ image_url: null })
        .eq("family_id", familyId);
      if (error) {
        setState({ phase: "error", message: "無法移除家徽：" + error.message });
        return false;
      }
      setState({ phase: "idle" });
      return true;
    },
    [],
  );

  return { state, upload, remove, reset };
}
